import { useState } from "react";
import { HandHeart, Shirt, MapPin, Baby, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CHURCH, IMAGES, SERVICE_OPTIONS } from "../lib/data";
import { PageHeader } from "../components/PageHeader";
import { Eyebrow, Field, Btn } from "../components/ui-kit";
import { Reveal } from "../lib/motion";
import { api } from "../lib/api";

const EXPECT = [
  { icon: HandHeart, t: "What Should I Expect?", d: "A warm welcome, meaningful worship, biblical teaching, heartfelt prayer, genuine fellowship, and a loving church family that's genuinely glad you came." },
  { icon: Shirt, t: "What Should I Wear?", d: "Come as you are! Whether that's jeans or your Sunday best, wear whatever makes you feel comfortable. We care about you, not your outfit." },
  { icon: MapPin, t: "Where Do I Go?", d: `We're located at ${CHURCH.address}, ${CHURCH.cityStateZip}. Free parking is available on-site, and our greeters will help you find your way.` },
  { icon: Baby, t: "What About My Children?", d: "Your kids are going to love it here. We're building safe, fun, age-appropriate programs — nursery, children's ministry, and youth. Ask a greeter for details." },
];

export default function PlanVisit() {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", adults: 1, children: 0, preferred_service: "", comments: "" });
  const [loading, setLoading] = useState(false);
  const ch = (e) => setF({ ...f, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/forms/visit", { ...f, adults: parseInt(f.adults) || 1, children: parseInt(f.children) || 0 });
      toast.success(data.message);
      setF({ first_name: "", last_name: "", email: "", phone: "", adults: 1, children: 0, preferred_service: "", comments: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    setLoading(false);
  };

  return (
    <div data-testid="plan-visit-page">
      <PageHeader eyebrow="First Time?" title={"Plan Your\nVisit"} sub="We can't wait to meet you. Here's everything you need to know before your first visit to One Love Ministries." image={IMAGES.welcome} />

      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid sm:grid-cols-2 gap-8">
          {EXPECT.map((x, i) => (
            <Reveal key={x.t} delay={(i % 2) * 0.08} className="p-8 sm:p-10 border border-black/10 rounded-sm hover:border-[#E63946] transition-colors">
              <x.icon className="text-[#E63946] mb-5" size={28} />
              <h3 className="font-display font-bold text-2xl mb-3">{x.t}</h3>
              <p className="text-black/60 leading-relaxed">{x.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Map */}
      <section className="pb-24 sm:pb-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal className="overflow-hidden rounded-sm border border-black/10 h-[380px]">
            <iframe title="Directions" src={CHURCH.mapEmbed} className="w-full h-full" style={{ border: 0 }} loading="lazy" />
          </Reveal>
        </div>
      </section>

      {/* I'm New form */}
      <section className="py-24 sm:py-32 bg-[#F4F4F5]">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8">
          <Reveal className="text-center mb-12">
            <Eyebrow>I'm New</Eyebrow>
            <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl mb-4">Let us know you're coming.</h2>
            <p className="text-black/60 text-lg">Tell us a little about yourself and we'll be ready to welcome you and your family.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="bg-white rounded-sm p-7 sm:p-10 border border-black/10" data-testid="visit-form">
              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <Field label="First Name" name="first_name" required value={f.first_name} onChange={ch} />
                <Field label="Last Name" name="last_name" required value={f.last_name} onChange={ch} />
                <Field label="Email" name="email" type="email" required value={f.email} onChange={ch} />
                <Field label="Phone" name="phone" value={f.phone} onChange={ch} />
                <Field label="Adults Attending" name="adults" type="number" min="1" value={f.adults} onChange={ch} />
                <Field label="Children Attending" name="children" type="number" min="0" value={f.children} onChange={ch} />
              </div>
              <div className="mb-6"><Field label="Preferred Service" name="preferred_service" options={SERVICE_OPTIONS} value={f.preferred_service} onChange={ch} /></div>
              <div className="mb-8"><Field label="Questions or Comments" name="comments" textarea value={f.comments} onChange={ch} /></div>
              <Btn type="submit" onClick={() => {}} testid="visit-submit" disabled={loading} className="w-full">{loading ? <><Loader2 className="animate-spin" size={16} /> Sending…</> : "Let Us Know You're Coming"}</Btn>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
